import React from 'react';
import { EmptyState, type EmptyStateAction, type EmptyStateProps } from '@prerender/design-system/EmptyState';

import { useEvent } from '~features/events/hooks/useEvent';
import { useIsPrinting } from '~library/print';
import {
  EMPTY_STATE_ACTION_CLICKED_EVENT,
  EMPTY_STATE_VIEWED_EVENT,
  type EmptyStateActionClickedPayload,
  type EmptyStateKind,
  type EmptyStatePlacement,
  type EmptyStateScreen,
  type EmptyStateVariant,
  type EmptyStateViewedPayload,
  type FilterEmptyVariant,
  type NoDataVariant,
  emptyStateKindOf,
} from './EmptyStateEvents';

export type TrackedEmptyStateAction =
  | { id: string; label: string; onClick: () => void; destination: string; href?: never }
  | { id: string; label: string; href: string; onClick?: never; destination?: never };

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

type WithVariant<T> = T extends { state: 'filtered' }
  ? T & { variant: FilterEmptyVariant }
  : T extends { state: 'no-data' }
  ? T & { variant?: NoDataVariant }
  : T & { variant?: never };

export type TrackedEmptyStateProps = WithVariant<DistributiveOmit<EmptyStateProps, 'action' | 'secondaryAction'>> & {
  screen: EmptyStateScreen;
  action: TrackedEmptyStateAction;
  secondaryAction?: TrackedEmptyStateAction;
};

type LinkAction = Extract<TrackedEmptyStateAction, { href: string }>;

function isLink(action: TrackedEmptyStateAction): action is LinkAction {
  return typeof action.href === 'string';
}

// Not `window.open(url, '_blank', 'noopener,noreferrer')`: a features string can open a popup window instead of a tab.
function openInNewTab(href: string): void {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.click();
}

function viewedPayload(
  screen: EmptyStateScreen,
  state: EmptyStateKind,
  placement: EmptyStatePlacement,
  variant: EmptyStateVariant | undefined
): EmptyStateViewedPayload {
  return variant ? { screen, state, placement, variant } : { screen, state, placement };
}

function TrackedEmptyState({ screen, action, secondaryAction, variant, ...emptyState }: TrackedEmptyStateProps) {
  const { track } = useEvent();
  const { isPrinting } = useIsPrinting();
  const state = emptyStateKindOf(emptyState.state);
  const placement: EmptyStatePlacement = emptyState.compact ? 'widget' : 'page';
  const viewedKey = `${screen}:${state}:${placement}:${variant ?? ''}`;
  const lastViewedKey = React.useRef<string>();

  React.useEffect(() => {
    if (isPrinting || lastViewedKey.current === viewedKey) return;
    lastViewedKey.current = viewedKey;
    track(EMPTY_STATE_VIEWED_EVENT, viewedPayload(screen, state, placement, variant));
  }, [viewedKey, screen, state, placement, variant, track, isPrinting]);

  const trackedAction = (tracked: TrackedEmptyStateAction): EmptyStateAction => {
    const destination = isLink(tracked) ? tracked.href : tracked.destination;
    const activate = isLink(tracked) ? () => openInNewTab(tracked.href) : tracked.onClick;

    return {
      label: tracked.label,
      onClick: () => {
        const payload: EmptyStateActionClickedPayload = {
          ...viewedPayload(screen, state, placement, variant),
          action_id: tracked.id,
          destination,
        };
        track(EMPTY_STATE_ACTION_CLICKED_EVENT, payload);
        activate();
      },
    };
  };

  const props = {
    ...emptyState,
    action: trackedAction(action),
    secondaryAction: secondaryAction ? trackedAction(secondaryAction) : undefined,
  } as EmptyStateProps;

  return <EmptyState {...props} />;
}

export default TrackedEmptyState;
